import {Icon, Show, Space} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Action} from '../Actions/Actions';
import {Badge, BadgeProps} from '../Badge/Badge';
import {TabIconVariant} from './TabIconVariant';
import {getIconPropsFromVariant} from './utils/getIconPropsFromVariant';

export type TabVariants = 'container' | 'condensed' | 'fullHeight' | 'default';

export type TabBadge = Pick<BadgeProps, 'colorScheme' | 'value' | 'icon'>;

export type TabProps = TestIdProps & {
  id: string;
  title: string;
  content: ReactNode;
  isDisabled?: boolean;
  icon?: TabIconVariant;
  badge?: TabBadge;
  actions?: Action[];
  onClick?: (id: string) => void;
  onFocus?: (id: string) => void;
  isSelected?: boolean;
  isFocused?: boolean;
  variant?: TabVariants;
  isFullWidth?: boolean;
  tabId?: string;
  panelId?: string;
  tabIndex?: number;
  ariaLabel?: string;
  ariaDescription?: string;
};

const tabVariants = cva(
  'flex select-none items-center justify-start border-solid p-0 text-left text-[length:var(--text-size-base)] font-[var(--font-weight-medium)] leading-[var(--text-leading-base)] text-[var(--color-primary)] outline-0 disabled:cursor-not-allowed disabled:border-transparent disabled:text-[var(--color-palettes-neutral-900-40)] disabled:active:border-transparent',
  {
    variants: {
      variant: {
        container:
          'border-t-2 px-4 pb-4 pt-[14px] text-[var(--color-primary)] hover:bg-[var(--color-palettes-neutral-20-100)] focus-visible:border-0 focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] focus-visible:outline-offset-2 focus:not(:focus-visible):outline-none disabled:border-[var(--color-palettes-neutral-10-100)] disabled:bg-[var(--color-palettes-neutral-10-100)]',
        condensed:
          'border-b-2 pb-[6px] pt-0 hover:active:border-b-2 hover:active:border-b-[var(--color-palettes-neutral-100-100)] focus-visible:border-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus:not(:focus-visible):outline-none',
        fullHeight:
          'border-b-2 pb-[6px] pt-0 hover:active:border-b-2 hover:active:border-b-[var(--color-palettes-neutral-100-100)] focus-visible:border-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus:not(:focus-visible):outline-none',
        default:
          'border-b-2 pb-[14px] pt-4 hover:active:border-b-[var(--color-palettes-neutral-100-100)] focus-visible:border-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus:not(:focus-visible):outline-none',
      },
      isFullWidth: {
        true: 'flex-[100%]',
        false: 'flex-none',
      },
      isDisabled: {
        true: 'pointer-events-none',
        false: 'pointer-events-auto cursor-pointer',
      },
    },
    defaultVariants: {
      variant: 'default',
      isFullWidth: false,
      isDisabled: false,
    },
  }
);

const screenReaderOnlyVariants = cva(
  'absolute m-[-1px] h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)]'
);

function getTabStateClassName(props: Pick<TabProps, 'variant' | 'isSelected' | 'isFocused'>) {
  if (props.variant === 'container') {
    return cn(
      props.isSelected
        ? 'border-t-[var(--color-palettes-blue-60-100)] bg-[var(--color-palettes-white-10-100)]'
        : 'border-t-transparent bg-[var(--color-palettes-neutral-10-100)]',
      props.isFocused ? 'focus-visible:bg-[var(--color-palettes-neutral-20-100)]' : undefined
    );
  }

  return cn(
    match({isSelected: Boolean(props.isSelected), isFocused: Boolean(props.isFocused)})
      .with(
        {isSelected: true},
        () =>
          'border-b-[var(--color-accent)] text-[var(--color-accent)] hover:border-b-[var(--color-accent)] focus-visible:outline-[var(--color-accent)]'
      )
      .with(
        {isFocused: true},
        () =>
          'border-b-[var(--color-palettes-neutral-50-100)] text-[var(--color-palettes-neutral-70-100)] hover:border-b-[var(--color-palettes-neutral-50-100)] focus-visible:outline-[var(--color-palettes-neutral-50-100)]'
      )
      .otherwise(
        () =>
          'border-b-transparent hover:border-b-[var(--color-palettes-neutral-50-100)] focus-visible:outline-[var(--color-palettes-neutral-50-100)]'
      )
  );
}

/**
 * @internal
 */
export function Tab(props: Omit<TabProps, 'content' | 'actions'>) {
  const tabIcon = getIconPropsFromVariant(props.icon);

  const handleClick = () => {
    if (props.isDisabled) {
      return;
    }
    props.onClick?.(props.id);
  };

  const handleFocus = () => {
    if (props.isDisabled) {
      return;
    }
    // Only update focus state, don't activate the tab
    props.onFocus?.(props.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    // Space and Enter should activate the tab
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleClick();
    }
  };

  // Generate accessible label combining title with optional aria label
  const accessibleLabel = props.ariaLabel || `${props.title}`;

  // Create description text for screen readers
  const descriptionText =
    props.ariaDescription ||
    `${props.isSelected ? 'Currently selected' : 'Not selected'} tab${props.isDisabled ? ', disabled' : ''}`;

  return (
    <button
      id={props.tabId}
      role="tab"
      onClick={handleClick}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      className={cn(
        tabVariants({
          variant: props.variant,
          isFullWidth: props.isFullWidth,
          isDisabled: props.isDisabled,
        }),
        getTabStateClassName(props)
      )}
      data-testid={suffixTestId('tab', props)}
      data-tab-id={props.id}
      aria-label={accessibleLabel}
      aria-describedby={props.ariaDescription ? `${props.tabId}-desc` : undefined}
      aria-selected={props.isSelected}
      aria-disabled={props.isDisabled}
      aria-controls={props.panelId}
      aria-current={props.isSelected ? 'page' : undefined}
      disabled={props.isDisabled}
      tabIndex={props.tabIndex ?? (props.isSelected ? 0 : -1)}
      type="button"
      title={props.isDisabled ? `${props.title} (disabled)` : props.title}
    >
      <Show when={isNotNil(tabIcon)}>
        <span className="mr-3">
          <Icon
            value={tabIcon?.key}
            color={tabIcon?.color}
            data-testid={suffixTestId('tabIcon', props)}
            aria-hidden="true"
          />
        </span>
      </Show>
      {props.title}
      <Show when={props.badge}>
        {(badge) => (
          <>
            <Space horizontal={2} />
            <Show when={badge.value}>
              {(value) => (
                <Badge
                  size="small"
                  variant="bold"
                  colorScheme={badge.colorScheme}
                  value={value}
                  data-testid={suffixTestId('tabBadge', props)}
                />
              )}
            </Show>
            <Show when={badge.icon}>
              {(icon) => (
                <Badge
                  size="small"
                  variant="bold"
                  colorScheme={badge.colorScheme}
                  icon={icon}
                  data-testid={suffixTestId('tabBadge', props)}
                />
              )}
            </Show>
          </>
        )}
      </Show>
      <Show when={isNotNilOrEmpty(props.ariaDescription)}>
        <span id={`${props.tabId}-desc`} className={cn(screenReaderOnlyVariants())}>
          {descriptionText}
        </span>
      </Show>
    </button>
  );
}
